import { AlertColor } from '@mui/material'
import { produce } from 'immer'
import { DateTime } from 'luxon'
import { ReactElement } from 'react'
import { create, type StoreApi } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { UseBoundStore } from 'zustand/react'

export type SnackBarAlert = {
  openSnackBar: boolean
  variant?: 'filled' | 'outlined' | 'standard'
  severity: AlertColor
  message: string
  autoHideDuration?: number | null
}

export type DialogCloseConfirmation = {
  title: string
  body: string
  onAfterConfirm?: () => void
}

export type DialogModal = {
  titleDialog: string | null
  open: boolean
  content: ReactElement | null
  closeConfirmation?: DialogCloseConfirmation | null
  pendingCloseConfirmation?: boolean
  maxWidth?: number
}

const createEmptyDialogModal = (): DialogModal => ({
  titleDialog: null,
  open: false,
  content: null,
  closeConfirmation: null,
  pendingCloseConfirmation: false,
})

export type InitialGlobalState = {
  height: number | null
  dialogModal: DialogModal
  snackbarAlert: SnackBarAlert
  datePickerStartDate: DateTime
}

export type Global = InitialGlobalState & {
  setHeight?: (height: number) => void
  openDialog: (params: {
    titleDialog: string | null
    content: React.ReactNode
    closeConfirmation?: DialogCloseConfirmation | null
    maxWidth?: number
  }) => void
  closeDialog: () => void
  requestCloseDialog: () => void
  dismissCloseConfirmation: () => void
  confirmCloseDialog: () => void
  openSnackbarAlert?: (values: SnackBarAlert) => void
  closeSnackbarAlert?: () => void
}

const minimumSaviooDate = DateTime.fromObject(
  {
    year: 1990,
    month: 1,
    day: 1,
    hour: 0,
    minute: 0,
  },
  { zone: 'utc' },
)

const initialGlobalState: InitialGlobalState = {
  height: null,
  dialogModal: createEmptyDialogModal(),
  snackbarAlert: {
    openSnackBar: false,
    variant: 'filled',
    severity: 'info',
    message: '',
    autoHideDuration: null,
  },
  datePickerStartDate: minimumSaviooDate,
}

export const globalStore = create<Global>()(
  devtools(
    (set) => ({
      ...initialGlobalState,
      openDialog: ({ titleDialog, content, closeConfirmation = null, maxWidth }) =>
        set(
          produce((draft: Global) => {
            draft.dialogModal.open = true
            draft.dialogModal.titleDialog = titleDialog
            draft.dialogModal.content = content as ReactElement
            draft.dialogModal.closeConfirmation = closeConfirmation
            draft.dialogModal.maxWidth = maxWidth
            draft.dialogModal.pendingCloseConfirmation = false
          }),
          false,
          'openDialog',
        ),
      closeDialog: () =>
        set(
          produce((draft: Global) => {
            draft.dialogModal = createEmptyDialogModal()
          }),
          false,
          'closeDialog',
        ),
      requestCloseDialog: () =>
        set(
          produce((draft: Global) => {
            if (!draft.dialogModal.open) return
            if (draft.dialogModal.closeConfirmation) {
              draft.dialogModal.pendingCloseConfirmation = true
            } else {
              draft.dialogModal = createEmptyDialogModal()
            }
          }),
          false,
          'requestCloseDialog',
        ),
      dismissCloseConfirmation: () =>
        set(
          produce((draft: Global) => {
            draft.dialogModal.pendingCloseConfirmation = false
          }),
          false,
          'dismissCloseConfirmation',
        ),
      confirmCloseDialog: () => {
        let onAfterConfirm: (() => void) | undefined
        set(
          produce((draft: Global) => {
            onAfterConfirm = draft.dialogModal.closeConfirmation?.onAfterConfirm
            draft.dialogModal = createEmptyDialogModal()
          }),
          false,
          'confirmCloseDialog',
        )
        onAfterConfirm?.()
      },
      setHeight: (height) =>
        set(
          produce((draft: Global) => {
            draft.height = height
          }),
          false,
          'setHeight',
        ),
      openSnackbarAlert: ({ severity, message, autoHideDuration }) =>
        set(
          produce((draft: Global) => {
            draft.snackbarAlert.openSnackBar = true
            draft.snackbarAlert.severity = severity
            draft.snackbarAlert.message = message
            draft.snackbarAlert.autoHideDuration = autoHideDuration ?? 2000
          }),
          false,
          'openSnackbarAlert',
        ),
      closeSnackbarAlert: () =>
        set(
          produce((draft: Global) => {
            draft.snackbarAlert.openSnackBar = false
          }),
          false,
          'closeSnackbarAlert',
        ),
    }),
    { name: 'Global' },
  ),
) as unknown as UseBoundStore<StoreApi<Global>>
